import React, { useState } from "react";
import { X } from "lucide-react";
import {
  Expense,
  CreateExpenseData,
  UpdateExpenseData,
  ExpenseCategory,
  PaymentMethod,
  RecurringFrequency,
} from "../../types/expense";

interface ExpenseFormProps {
  expense?: Expense | null;
  onSubmit: (data: CreateExpenseData | UpdateExpenseData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface FormData {
  description: string;
  category: ExpenseCategory;
  subcategory: string;
  amount: number;
  date: string;
  vendorName: string;
  vendorPhone: string;
  vendorEmail: string;
  vendorAddress: string;
  paymentMethod: PaymentMethod;
  paymentReference: string;
  receiptNumber: string;
  budgetCategory: string;
  isRecurring: boolean;
  recurringFrequency: RecurringFrequency;
  recurringEndDate: string;
  tags: string;
  notes: string;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  expense,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<FormData>({
    description: expense?.description || "",
    category: expense?.category || "Supplies",
    subcategory: expense?.subcategory || "",
    amount: expense?.amount || 0,
    date: expense?.date
      ? expense.date.split("T")[0]
      : new Date().toISOString().split("T")[0],
    vendorName: expense?.vendor?.name || "",
    vendorPhone: expense?.vendor?.contact?.phone || "",
    vendorEmail: expense?.vendor?.contact?.email || "",
    vendorAddress: expense?.vendor?.contact?.address || "",
    paymentMethod: expense?.paymentMethod || "cash",
    paymentReference: expense?.paymentReference || "",
    receiptNumber: expense?.receiptNumber || "",
    budgetCategory: expense?.budgetCategory || "",
    isRecurring: expense?.isRecurring || false,
    recurringFrequency: expense?.recurringDetails?.frequency || "monthly",
    recurringEndDate: expense?.recurringDetails?.endDate?.split("T")[0] || "",
    tags: expense?.tags?.join(", ") || "",
    notes: expense?.notes || "",
  });

  const expenseCategories = [
    { value: "Salaries", label: "Salaries" },
    { value: "Utilities", label: "Utilities" },
    { value: "Maintenance", label: "Maintenance" },
    { value: "Supplies", label: "Supplies" },
    { value: "Transport", label: "Transport" },
    { value: "Marketing", label: "Marketing" },
    { value: "Insurance", label: "Insurance" },
    { value: "Equipment", label: "Equipment" },
    { value: "Food & Catering", label: "Food & Catering" },
    { value: "Professional Services", label: "Professional Services" },
    { value: "Rent", label: "Rent" },
    { value: "Communication", label: "Communication" },
    { value: "Training", label: "Training" },
    { value: "Technology", label: "Technology" },
    { value: "Security", label: "Security" },
    { value: "Cleaning", label: "Cleaning" },
    { value: "Medical", label: "Medical" },
    { value: "Miscellaneous", label: "Miscellaneous" },
  ];

  const paymentMethods = [
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "mpesa", label: "Mobile Money (M-Pesa)" },
    { value: "card", label: "Credit/Debit Card" },
    { value: "other", label: "Other" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.amount <= 0) {
      alert("Amount must be greater than zero");
      return;
    }

    if (!formData.description.trim()) {
      alert("Description is required");
      return;
    }

    const tagsArray = formData.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    const submitData: CreateExpenseData | UpdateExpenseData = {
      description: formData.description.trim(),
      category: formData.category,
      subcategory: formData.subcategory.trim() || undefined,
      amount: formData.amount,
      date: formData.date,
      vendor: {
        name: formData.vendorName.trim() || undefined,
        contact: {
          phone: formData.vendorPhone.trim() || undefined,
          email: formData.vendorEmail.trim() || undefined,
          address: formData.vendorAddress.trim() || undefined,
        },
      },
      paymentMethod: formData.paymentMethod,
      paymentReference: formData.paymentReference.trim() || undefined,
      receiptNumber: formData.receiptNumber.trim() || undefined,
      budgetCategory: formData.budgetCategory.trim() || undefined,
      isRecurring: formData.isRecurring,
      recurringDetails: formData.isRecurring
        ? {
            frequency: formData.recurringFrequency as RecurringFrequency,
            endDate: formData.recurringEndDate || undefined,
          }
        : undefined,
      tags: tagsArray,
      notes: formData.notes.trim() || undefined,
    };

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {expense ? "Edit Expense" : "Add New Expense"}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of the expense"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    category: e.target.value as ExpenseCategory,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {expenseCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subcategory (Optional)
              </label>
              <input
                type="text"
                value={formData.subcategory}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    subcategory: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Specific subcategory"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (KSh) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      amount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, date: e.target.value }))
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Vendor Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Vendor Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor Name
                </label>
                <input
                  type="text"
                  value={formData.vendorName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vendorName: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Vendor or supplier name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.vendorPhone}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vendorPhone: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Vendor phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.vendorEmail}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vendorEmail: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Vendor email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.vendorAddress}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vendorAddress: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Vendor address"
                />
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Payment Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value as PaymentMethod,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Reference
                </label>
                <input
                  type="text"
                  value={formData.paymentReference}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      paymentReference: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Transaction ID, check number, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Receipt Number
                </label>
                <input
                  type="text"
                  value={formData.receiptNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      receiptNumber: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Receipt or invoice number"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Additional Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget Category
                </label>
                <input
                  type="text"
                  value={formData.budgetCategory}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      budgetCategory: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Budget category for tracking"
                />
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={formData.isRecurring}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isRecurring: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                />
                <div className="flex-1">
                  <label
                    htmlFor="isRecurring"
                    className="text-sm font-medium text-gray-700"
                  >
                    Recurring Expense
                  </label>
                  <p className="text-xs text-gray-500">
                    Check if this is a recurring expense (e.g., rent, utilities)
                  </p>
                </div>
              </div>

              {formData.isRecurring && (
                <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency
                    </label>
                    <select
                      value={formData.recurringFrequency}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          recurringFrequency: e.target
                            .value as RecurringFrequency,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.recurringEndDate}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          recurringEndDate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, tags: e.target.value }))
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter tags separated by commas (e.g., urgent, maintenance, supplies)"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <div className="relative">
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Additional notes or comments"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {expense ? "Updating..." : "Creating..."}
                </div>
              ) : expense ? (
                "Update Expense"
              ) : (
                "Create Expense"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
